import * as React from 'react'

import { ResponsiveLine } from '@nivo/line'

import i18n from 'modules/core/lib/i18n'

import { formatterForGraph, validateFieldType } from 'modules/reports/lib/Constants'
import type ReportStruct from 'modules/reports/lib/ReportStruct'

interface Props {
  munched: any
  data: Array<Record<string, any>>
  reportStruct: ReportStruct
  showLegend: boolean
  chartData: Record<string, any>
}

/**
 * AreaChart uses the data structure from the Area muncher to render the chart.
 *
 * The Area muncher calculates the minimum values in order to render the X axis
 * from this minimum value.
 *
 * The area muncher internally uses the PointDimensions class. the PoinDimension class
 * generates an array of object with X and Y axis, in order to use it with charts that
 * requires a X and Y axis such as Area and Lines charts.
 *
 * Then Area muncher generates the processedData. the processedData contains
 * an array of object with the X and Y keys, these keys represents a point in the Area
 * to pain in the chart canvas, and finally the id key. The id keys is the projection
 * name of the Y axis.
 *
 * @example
 * // {
 * //   min: 1,
 * //   processedData: {
 * //    id: 'count',
 * //    data: [{x: '2022-08-01', y: 1}]
 * //   }
 * // }
 *
 * const data = {count.leave.id: 1, cohort: '2022-08-01'}
 * const aggregation = {
 *  function: "count",
 *  path: "leave.id"
 * }
 * const projection = {
 *  function: "count",
 *  path: "leave.id"
 * }
 *
 * const muncher = new Area({data, aggregation, projection})
 * muncher.call()
 *
 * @constructor
 * @param munched - provide data structure output from the Area muncher class
 * @param data - provide raw data from reports controller
 * @param reportStruct - provide reportStruct in order to read the query projections
 * @param showLegend - provide showLegend in order to show the chart legend
 * @param chartData - provide chartData in order to read the sql projections
 */
class AreaChart extends React.Component<Props> {
  /**
   * formatBottomAxis - formats each value of the X axis with the projection type format,
   * the formatter is not available for sql.
   *
   * This function reads the projection type and returns a formatter that matches the
   * projection type.
   *
   * The projections available formatters are: number, identifier, boolean, count, date,
   * cents, money, cohort, link, year, month, unit, hours, enum
   *
   * @method
   * @example
   * // returns "3,00"
   * formatBottonAxis(3.0)
   **/
  formatBottomAxis = (value: string) => {
    const projection = this.props.reportStruct.props.projections[1]

    if (projection && !validateFieldType(projection, value)) {
      return i18n.t('custom_reports.value_mismatch')
    }

    return projection && formatterForGraph(projection)(value)
  }

  /**
   * extraProps - returns the chart legends and its margin.
   * if showLegened is true and the reportStruct has projections available it will
   * return a hash in order to render the chart legends, however if there are not
   * projections available it return only the chart margins.
   * @method
   */
  extraProps(): any {
    const { showLegend, reportStruct, chartData } = this.props

    if (showLegend && (reportStruct.props.projections.length > 2 || chartData?.tertiary_data)) {
      return {
        margin: {
          top: 30,
          right: 150,
          bottom: 60,
          left: 80,
        },
        legends: [
          {
            dataFrom: 'keys',
            anchor: 'bottom-right',
            direction: 'column',
            justify: false,
            translateX: 120,
            translateY: 0,
            itemsSpacing: 2,
            itemWidth: 100,
            itemHeight: 20,
            itemDirection: 'left-to-right',
            itemOpacity: 0.85,
            symbolSize: 20,
          },
        ],
      }
    }

    return {
      margin: {
        top: 30,
        right: 50,
        bottom: 60,
        left: 80,
      },
    }
  }

  render() {
    const { munched, reportStruct } = this.props

    if (!munched) return null
    const { processedData, min } = munched

    const addFormat = () =>
      reportStruct.sqlQuery
        ? null
        : {
            format: this.formatBottomAxis,
          }

    return (
      <ResponsiveLine
        data={processedData}
        xScale={{
          type: 'point',
        }}
        yScale={{
          type: 'linear',
          min: 'auto',
          max: 'auto',
        }}
        axisBottom={{
          tickSize: 5,
          tickPadding: 5,
          ...addFormat(),
        }}
        enableArea
        lineWidth={2}
        areaBaselineValue={min}
        areaOpacity={0.6}
        animate
        curve="monotoneX"
        {...this.extraProps()}
      />
    )
  }
}

export default AreaChart
